/* eslint-disable no-console */
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

/*
 * We are loading this JS file, in via import-loader
 * This is because PDFTron expect the config file to be separate,
 *   and this is the easiest way we can do it.
 *
 * At this time we haven't figured out how to have this file be typescript.
 * It should be possible with webpack and with a sequence of loaders.
 */

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import BaseConfigJS from './pdftron-webviewer-config-base.static.js';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import SignConfigJS from './pdftron-webviewer-config-sign.static.js';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import CreateSignatureEnelopeConfigJS from './pdftron-webviewer-config-create-signature-envelope.static.js';

export const SKIP_URL_RESOLVE_PREFIX = 'no-url-resolve://';

import { authenticatedHttpRequest } from 'utils/http';
import { useColorMode } from '@cardboard-ui/react';
import { CenteredSpinner } from 'components/CenteredSpinner';
import WebViewer, { WebViewerOptions } from '@pdftron/webviewer';
import { ErrorBoundary } from 'utils/errors';
import { useFeatureFlag, useSession } from 'utils/sessionProvider';
import { useI18n } from 'utils/i18n';
import { FileRenderer_node$data } from '../__generated__/FileRenderer_node.graphql';
import { contentFromNode } from '..';
import { useNodeSecurity } from 'apps/TenantApp/hooks/useNodeSecurity';
import { UnsupportedFileRenderer } from '../UnsupportedFileRenderer';
import { RelativelyCenteredSpinner } from 'components/suspense/SuspendWithRelativelyCenteredSpinner';
import { postMessageWTC } from '../utils';
import { Capacitor } from '@capacitor/core';
import { useCardboardEnv } from 'utils/useCardboardEnv';

interface PDFFileRendererProps {
  node: FileRenderer_node$data;
  mode?: WebViewerComponentProps['mode'];
}

declare const PDFTRON_LICENCE: string | null;

export const PDFTronFileRenderer: FC<PDFFileRendererProps> = ({
  node,
  mode = 'reading',
}) => {
  const { fileNameWithExtension: fileName, url } = contentFromNode(node);
  const [loadError, setLoadError] = useState(false);
  const [realUrl, setRealUrl] = useState<string | undefined>();
  const { hasWatermark } = useNodeSecurity(node);
  const isSignaturesEnabled = useFeatureFlag('FEATURE_SIGNATURES');

  useEffect(() => {
    if (realUrl || loadError) {
      return;
    } else if (url && url.startsWith(SKIP_URL_RESOLVE_PREFIX)) {
      // <<-- Ugly hack to make storybook work
      setRealUrl(url.replace(SKIP_URL_RESOLVE_PREFIX, ''));
    } else if (url) {
      authenticatedHttpRequest(`${url}.json`, {})
        .then(async (r) => {
          if (r.status === 200) {
            const { url } = await r.json();
            if (typeof url === 'string') {
              setRealUrl(url as string);
            } else {
              setLoadError(true);
            }
          } else {
            setLoadError(true);
          }
        })
        .catch((e) => {
          setLoadError(true);
        });
    }
  }, [url, realUrl, loadError]);

  if (loadError) {
    return <UnsupportedFileRenderer hasError node={node} />;
  }

  if (!realUrl) {
    return <CenteredSpinner />;
  }

  return (
    <ErrorBoundary fallback={<UnsupportedFileRenderer hasError node={node} />}>
      <WebViewerComponent
        url={realUrl}
        filename={fileName}
        addWatermark={hasWatermark}
        mode={mode}
      />
    </ErrorBoundary>
  );
};

interface WebViewerComponentProps {
  url: string;
  filename: string;
  addWatermark: boolean;
  mode: 'reading' | 'prepareSignature' | 'signing';
}

declare global {
  interface Window {
    webviewerServerUrl?: string;
  }
}

const WebViewerComponent = ({
  url,
  filename,
  addWatermark,
  mode,
}: WebViewerComponentProps) => {
  const {
    PDFTRON_CONFIG_BASE_SHA1,
    PDFTRON_CONFIG_SIGN_SHA1,
    PDFTRON_CONFIG_CREATE_SIGN_SHA1,
    REACT_APP_ASSETS_DOMAIN,
  } = useCardboardEnv();

  const isPersistSavedSignaturesEnabled = useFeatureFlag(
    'FEATURE_PERSIST_SAVED_SIGNATURES',
  );
  const viewer = useRef<HTMLDivElement | null>(null);
  const [domReady, setDomReady] = useState(false);
  const [viewerLoaded, setViewerLoaded] = useState(false);
  const [documentLoaded, setDocumentLoaded] = useState(false);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const { colorMode } = useColorMode();
  const { member } = useSession();
  const { format } = useI18n();
  const watermarkData = {
    mark: 'Confidential',
    name: member?.name || '',
    email: member?.email || '',
    uuid: member?.id || '',
    time: format(new Date(), 'yyyy/MM/dd kk:mm:ss'),
    shouldRender: addWatermark,
  };
  const observeViewerLoaded = useCallback((event: any) => {
    if (event.data.type === 'crdbrd-ctow-viewerLoaded') {
      setViewerLoaded(true);
    }
    if (event.data.type === 'crdbrd-ctow-documentLoaded') {
      setDocumentLoaded(true);
    }
    if (event.data.type === 'crdbrd-ctow-passwordRequired') {
      setPasswordRequired(true);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', observeViewerLoaded);
    return () => window.removeEventListener('message', observeViewerLoaded);
  }, [observeViewerLoaded]);

  // In the useEffect below this one, we ran into a race condition
  //   where `viewer.current` would be nil at the first render
  //   but by the tie the effect ran, it would be filled in.
  // This triggers the effect to run twice, and that is not wha twe want.
  // This little hack fixes that.
  useEffect(() => {
    setDomReady((s) => s || !!viewer.current);
  }, [viewer.current]);

  useEffect(() => {
    if (domReady && viewer.current) {
      let webviewerInitConig = {
        webviewerServerURL: window.webviewerServerUrl,
        path: generateWebViewerPath(
          '/webviewer-10.0.1',
          REACT_APP_ASSETS_DOMAIN,
        ),
      } as WebViewerOptions;

      if (mode === 'reading') {
        webviewerInitConig = {
          ...webviewerInitConig,
          config: getConfigFile(
            BaseConfigJS,
            'pdftron-webviewer-config-base',
            PDFTRON_CONFIG_BASE_SHA1,
            REACT_APP_ASSETS_DOMAIN,
          ),
          annotationUser: `${member?.id || 'anonymous'}-reader`,
          fullAPI: true,
        };
      } else if (mode === 'prepareSignature') {
        if (!member) {
          throw new Error('prepareSignature requires given member');
        }
        webviewerInitConig = {
          ...webviewerInitConig,
          config: getConfigFile(
            CreateSignatureEnelopeConfigJS,
            'pdftron-webviewer-config-create-signature-envelope',
            PDFTRON_CONFIG_CREATE_SIGN_SHA1,
            REACT_APP_ASSETS_DOMAIN,
          ),
          annotationUser: `${member.id}-signaturePreparer`,
          fullAPI: true,
        };
      } else if (mode === 'signing') {
        if (!member) {
          throw new Error('signing requires given member');
        }
        webviewerInitConig = {
          ...webviewerInitConig,
          config: getConfigFile(
            SignConfigJS,
            'pdftron-webviewer-config-sign',
            PDFTRON_CONFIG_SIGN_SHA1,
            REACT_APP_ASSETS_DOMAIN,
          ),
          annotationUser: member.id,
          fullAPI: true,
        };
      }

      WebViewer(webviewerInitConig, viewer.current).catch(() => {});
    }
  }, [domReady]);

  useEffect(() => {
    if (viewerLoaded && viewer.current) {
      postMessageWTC({
        type: 'loadDocument',
        url: url,
        filename: filename,
        l: PDFTRON_LICENCE,
      });
    }
  }, [viewerLoaded, viewer.current]);

  useEffect(() => {
    if (viewer.current) {
      postMessageWTC({
        type: 'changeColorMode',
        colorMode,
      });
    }
  }, [viewer.current, colorMode]);

  useEffect(() => {
    if (viewerLoaded && viewer.current) {
      postMessageWTC({
        type: 'addWatermark',
        ...watermarkData,
      });
    }
    // Adding documentLoaded here, to be sure to send the command again when it is done
    // It is unclear in the docs if a watermark call should happen before or after document loaded.
  }, [viewer.current, addWatermark, documentLoaded]);

  useEffect(() => {
    if (viewerLoaded && viewer.current) {
      postMessageWTC({
        type: 'persistSavedSignatures',
        isEnabled: isPersistSavedSignaturesEnabled,
      });
    }
  }, [viewerLoaded, viewer.current, isPersistSavedSignaturesEnabled]);

  return (
    <>
      <div
        id="web-viewer"
        data-qa={`${mode}-mode`}
        data-chromatic="ignore"
        ref={viewer}
        style={{ height: '100%', width: '100%', marginTop: 0 }}
      >
        {!passwordRequired && !documentLoaded && <RelativelyCenteredSpinner />}
      </div>
    </>
  );
};

const generateWebViewerPath = (path: string, location: string | null) => {
  if (!path.startsWith('/')) {
    throw new Error('WebViewerPath should always start with a /');
  }

  if (Capacitor.isNativePlatform() && location) {
    return `${location}${path}`;
  }

  if (__webpack_public_path__.endsWith('/')) {
    return `${__webpack_public_path__.slice(0, -1)}${path}`;
  } else {
    return `${__webpack_public_path__}${path}`;
  }
};

function getConfigFile(
  localFile: string,
  fileName: string,
  sha1: string | null,
  location: string | null,
) {
  if (Capacitor.isNativePlatform() && sha1 && location) {
    return `${location}/${fileName}.${sha1}.static.js`;
  } else {
    return localFile;
  }
}
